<template>
  <b-modal
    :active="value"
    :width="600"
    @close="close"
  >
    <div class="modal-card contact-agents-modal">
      <v-form
        ref="form"
        :data="form"
        format="formData"
        :url="$laravel.route('public.contact')"
        method="post"
        @success="onSuccess"
      >
        <template #default="{ errors }">
        <header
          class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
        >
          <h2 class="title is-5">
            {{ $t("contact_agent").capitalize() }}
          </h2>
        </header>
        <section class="modal-card-body">
          <v-input
            v-model="form.name"
            type="text"
            name="name"
            class="required-field"
          />
          <v-input
            v-model="form.email"
            type="email"
            name="email"
            class="required-field"
          />
          <v-input
            v-model="form.phone"
            type="text"
            name="phone"
            class="required-field"
          />

          <!-- Desired move-in date: same 3 modes as the listing filter -->
          <div class="field">
            <label class="label required-label">{{ $t("contact_move_in_label") }}</label>
            <div class="availability-modes">
              <b-radio
                v-model="form.move_in_mode"
                native-value="flexible"
              >
                {{ $t("availability_mode_flexible") }}
              </b-radio>
              <b-radio
                v-model="form.move_in_mode"
                native-value="fixed"
              >
                {{ $t("availability_mode_fixed") }}
              </b-radio>
              <b-radio
                v-model="form.move_in_mode"
                native-value="next30"
              >
                {{ $t("availability_next_30_days") }}
              </b-radio>
            </div>
            <b-datepicker
              v-if="form.move_in_mode === 'fixed'"
              v-model="moveInDate"
              expanded
              icon="calendar-today"
              :min-date="minMoveInDate"
              :placeholder="$t('availability_pick_date')"
              :mobile-native="false"
              append-to-body
            />
            <p
              v-if="errors.move_in_date"
              class="help is-danger"
            >
              {{ errors.move_in_date[0] }}
            </p>
          </div>

          <!-- Number of occupants -->
          <div class="field">
            <label class="label required-label">{{ $t("contact_occupants_label") }}</label>
            <div class="columns is-mobile mb-0">
              <div class="column pb-0">
                <b-field
                  :label="$t('contact_adults')"
                  :type="errors.adults ? 'is-danger' : ''"
                  :message="errors.adults ? errors.adults[0] : ''"
                >
                  <b-select
                    v-model="form.adults"
                    expanded
                  >
                    <option
                      v-for="opt in occupantOptions"
                      :key="opt"
                      :value="opt"
                    >
                      {{ opt }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="column pb-0">
                <b-field
                  :label="$t('contact_children')"
                  :type="errors.children ? 'is-danger' : ''"
                  :message="errors.children ? errors.children[0] : ''"
                >
                  <b-select
                    v-model="form.children"
                    expanded
                  >
                    <option
                      v-for="opt in occupantOptions"
                      :key="opt"
                      :value="opt"
                    >
                      {{ opt }}
                    </option>
                  </b-select>
                </b-field>
              </div>
            </div>
          </div>

          <!-- Pets -->
          <v-input
            v-model="form.animals"
            type="textarea"
            name="animals"
            :label="$t('contact_animals_label')"
          />

          <v-input
            v-model="form.other_information"
            type="textarea"
            name="other_information"
          />
        </section>
        <footer class="modal-card-foot">
          <button
            class="button"
            type="button"
            @click="close"
          >
            {{ $t("cancel") }}
          </button>

          <button
            class="button is-primary ml-3"
            type="submit"
          >
            {{ $t("send") }}
          </button>
        </footer>
        </template>
      </v-form>
    </div>
  </b-modal>
</template>

<script>
export default {
  props: {
    // v-model: whether the modal is shown
    value: {
      type: Boolean,
      default: false,
    },
    propertyId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      moveInDate: null,
      form: {
        name: null,
        email: null,
        phone: null,
        move_in_mode: "flexible",
        move_in_date: null,
        adults: null,
        children: null,
        animals: null,
        other_information: null,
        property_id: this.propertyId,
      },
    };
  },
  computed: {
    // 1, 2, 3, "4 et +"
    occupantOptions() {
      return ["0", "1", "2", "3", this.$t("occupants_4_plus")];
    },
    minMoveInDate() {
      return this.$dayjs().startOf("day").toDate();
    },
  },
  watch: {
    propertyId(value) {
      this.form.property_id = value;
    },
    // Serialize the picked date for the API
    moveInDate(value) {
      this.form.move_in_date = value
        ? this.$dayjs(value).format("YYYY-MM-DD")
        : null;
    },
    "form.move_in_mode"(value) {
      if (value !== "fixed") {
        this.moveInDate = null;
      }
    },
  },
  methods: {
    onSuccess() {
      this.close();
    },
    close() {
      this.form.name = null;
      this.form.email = null;
      this.form.phone = null;
      this.form.move_in_mode = "flexible";
      this.form.move_in_date = null;
      this.form.adults = null;
      this.form.children = null;
      this.form.animals = null;
      this.form.other_information = null;
      this.moveInDate = null;
      this.$emit("input", false);
    },
  },
};
</script>

<style lang="scss" scoped>
// Bulma's .modal-card flex layout assumes head/body/foot are direct children;
// the <form> wrapper breaks that, so the footer (Envoyer) could end up below
// the viewport. Recreate the layout on the form so the body scrolls instead.
.contact-agents-modal {
  max-height: calc(100vh - 6rem);

  form {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 6rem);
    min-height: 0;
  }

  .modal-card-body {
    overflow-y: auto;
    min-height: 0;
  }
}

// Red required star on the 5 mandatory fields (Nom → Nombre d'occupants)
.required-label::after,
.contact-agents-modal ::v-deep .required-field .label::after {
  content: " *";
  color: #e02b2b;
}

// Stack the 3 move-in choices vertically, like the listing filter
.availability-modes {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.6rem;
}
</style>
